// The genome's choreography, ported from native's `ConstellationTiming`
// (iOS `AgentConstellation.swift`, Android `ConstellationTiming.kt` — both pinned by
// parity tests). Every number here is that file's; the web reel is the same motion
// at the same speed.
//
// The web design draws the dots bare (Figma `10261:86167`), so the DESKTOP genome
// keeps skill names as a hover affordance and skips native's names beat: the agents
// follow the structure reveal after native's `namesHideGapMs` breather. The MOBILE
// genome (`nameReel`) plays native's full sequence — the skill names fade in one by
// one, hold to be read, then fade out — before the team lands, so it matches the
// phone screens exactly (the reel is the arrival moment on a narrow layout). Every
// beat that IS played keeps native's exact duration, stagger and easing.

/** Length of the structure reveal: the rings sweep in while the skill dots fly out. */
export const STRUCTURE_REVEAL_MS = 1080;
/** Per-dot fly-out delay — dot `i` leaves `i × 45ms` after the first. */
export const SKILL_DOT_STAGGER_MS = 45;
/** Per-label fade-in delay in the names beat — label `i` fades in `i × 32ms` after the first. */
export const LABEL_STAGGER_MS = 32;
/** A skill name's fade duration, in and out (native `labelFadeMs`). */
export const LABEL_FADE_MS = 320;
/** A skill name pops in at this scale and shrinks to 1 as it lands (native `labelLandScale`). */
export const LABEL_LAND_SCALE = 1.35;
/** How long the fully-shown skill names hold to be read before they fade out. */
export const NAMES_READ_MS = 520;
/** Breather between the structure and the team arriving. */
export const NAMES_HIDE_GAP_MS = 90;
/** Per-node delay as the team lands one by one. */
export const AGENT_STAGGER_MS = 175;
/** Beat between the last agent and the Maestro. */
export const MAESTRO_GAP_MS = 150;
/** How long the Maestro holds before the figure settles at rest. */
export const MAESTRO_SETTLE_MS = 420;

/** A frame's grace before the reel starts, so the initial state paints first (native sleeps 40ms). */
export const REEL_START_MS = 40;

/** Ring sweep: 600ms each, ascending radii 80ms apart (native's per-ring `.delay(idx * 0.08)`). */
export const RING_SWEEP_MS = 600;
export const RING_SWEEP_STAGGER_MS = 80;

/** Element durations. Native uses springs; these are the matched cubic-beziers in `Genome.module.css`. */
export const SKILL_DOT_FLIGHT_MS = 520;
export const AGENT_NODE_LAND_MS = 400;
export const MAESTRO_LAND_MS = 440;

export interface GenomeTimeline {
  /** When the skill names begin fading in (only meaningful under `nameReel`). */
  namesAtMs: number;
  /** When the skill names begin fading out (only meaningful under `nameReel`). */
  namesHideAtMs: number;
  /** When the first agent node starts landing. */
  agentsAtMs: number;
  /** When the Maestro pops in, after the whole team. */
  maestroAtMs: number;
  /** When everything has settled and the figure is at rest. */
  settledAtMs: number;
}

/** Extra beats the MOBILE reel inserts between the structure and the team: the skill names
 *  fade in one by one, hold, then fade out. `skillCount` drives how long the labels take to
 *  all appear, exactly as native's `labelsAppearSpanMs`. */
export interface NameReelOptions {
  skillCount?: number;
  nameReel?: boolean;
}

/** The reel's beat boundaries for a given team size. Pass `{ nameReel, skillCount }` for the
 *  mobile genome, whose sequence includes native's names beat; the desktop genome omits it. */
export function genomeTimeline(nodeCount: number, opts: NameReelOptions = {}): GenomeTimeline {
  const { nameReel = false, skillCount = 0 } = opts;

  const namesAtMs = REEL_START_MS + STRUCTURE_REVEAL_MS;
  const labelsAppearSpanMs = Math.max(skillCount - 1, 0) * LABEL_STAGGER_MS + LABEL_FADE_MS;
  const namesHideAtMs = namesAtMs + labelsAppearSpanMs + NAMES_READ_MS;

  // Desktop skips the names beat: the team follows the structure after the breather. Mobile
  // waits for the names to appear, hold and fade out first (native's `agentsAtMs`).
  const agentsAtMs = nameReel
    ? namesHideAtMs + LABEL_FADE_MS + NAMES_HIDE_GAP_MS
    : REEL_START_MS + STRUCTURE_REVEAL_MS + NAMES_HIDE_GAP_MS;

  const agentsSpanMs = Math.max(nodeCount - 1, 0) * AGENT_STAGGER_MS;
  const maestroAtMs = agentsAtMs + agentsSpanMs + MAESTRO_GAP_MS;
  return { namesAtMs, namesHideAtMs, agentsAtMs, maestroAtMs, settledAtMs: maestroAtMs + MAESTRO_SETTLE_MS };
}
